<template>
  <section class="skidata-event-toggle">
    <div class="skidata-event-toggle__row">
      <div>
        <h2 class="skidata-event-toggle__title">SKIDATA Arena</h2>
        <p class="skidata-event-toggle__description">
          {{ adminText('workflowsAll.use_this_event_for_skidata_arena_access_contr_ba17d04', 'Use this event for SKIDATA Arena access control. Enabling it validates the integration configuration and starts an initial resync.') }}
        </p>
      </div>
      <v-switch
        :input-value="persistedEnabled"
        :disabled="saving"
        color="#ff4c00"
        hide-details
        inset
        class="skidata-event-toggle__switch"
        @change="requestToggle"
      />
    </div>
    <p class="skidata-event-toggle__admin-note">{{ adminText('workflowsAll.only_super_admins_can_change_this_setting_e32492f', 'Only super admins can change this setting.') }}</p>

    <v-dialog v-model="confirmationOpen" persistent max-width="520">
      <v-card>
        <v-card-title class="skidata-event-toggle__dialog-title">
          {{ workflowLabel(confirmationTitle) }}
        </v-card-title>
        <v-card-text>{{ workflowLabel(confirmationDescription) }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn flat :disabled="saving" @click="cancelToggle">{{ adminText('workflowsAll.cancel_77dfd21', 'Cancel') }}</v-btn>
          <v-btn color="primary" :loading="saving" :disabled="saving" @click="confirmToggle">
            {{ workflowLabel(confirmationAction) }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <simple-snackbar :open.sync="snackbar.open" :message-prop="workflowLabel(snackbar.messageProp)" :color="snackbar.color" />
  </section>
</template>

<script>
import WorkflowDisplay from '@/admin-i18n/workflows-all-display'
import { CONFIGURE_SKIDATA_EVENT } from '@/graphql/event.graphql'
import SimpleSnackbar from '@/components/shared/snackbars/simple-snackbar.vue'

export default {
  mixins: [WorkflowDisplay],
  name: 'skidata-event-toggle',
  components: { SimpleSnackbar },
  props: {
    eventId: {
      type: [String, Number],
      required: true,
    },
    enabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      persistedEnabled: this.enabled,
      requestedEnabled: null,
      confirmationOpen: false,
      saving: false,
      snackbar: { open: false, color: 'primary', messageProp: '' },
    }
  },
  computed: {
    confirmationTitle() {
      return this.requestedEnabled ? this.adminText('workflowsAll.enable_skidata_arena_for_this_event_eb993d3', 'Enable SKIDATA Arena for this event?') : this.adminText('workflowsAll.disable_skidata_arena_9338161', 'Disable SKIDATA Arena?')
    },
    confirmationDescription() {
      return this.requestedEnabled
        ? this.adminText('workflowsAll.this_immediately_queues_an_initial_resync_exi_ade1373', 'This immediately queues an initial resync. Existing eligible tickets may be sent to SKIDATA.')
        : this.adminText('workflowsAll.disabling_can_be_rejected_after_a_successful__0809b58', 'Disabling can be rejected after a successful remote sync until Arena cleanup is confirmed.')
    },
    confirmationAction() {
      return this.requestedEnabled ? this.adminText('workflowsAll.enable_skidata_b32fcb1', 'Enable SKIDATA') : this.adminText('workflowsAll.disable_skidata_946c857', 'Disable SKIDATA')
    },
  },
  watch: {
    enabled(value) {
      if (!this.saving) this.persistedEnabled = value
    },
  },
  methods: {
    requestToggle(value) {
      this.requestedEnabled = value === true
      this.confirmationOpen = true
    },
    cancelToggle() {
      this.confirmationOpen = false
      this.requestedEnabled = null
    },
    async confirmToggle() {
      const requestedEnabled = this.requestedEnabled === true
      this.saving = true
      try {
        const { data, errors } = await this.$apollo.mutate({
          mutation: CONFIGURE_SKIDATA_EVENT,
          variables: {
            eventId: this.eventId,
            config: { enabled: requestedEnabled },
          },
        })
        const mutationError = errors && errors[0]
        if (mutationError) throw mutationError

        const updatedEvent = data && data.configureSkidataEvent
        if (!updatedEvent) throw new Error('SKIDATA Arena setting was not updated.')

        const metadata = updatedEvent.metadata || {}
        this.persistedEnabled = Boolean(metadata.skidata && metadata.skidata.enabled)
        this.$emit('updated', this.persistedEnabled)
        this.snackbar = {
          open: true,
          messageProp: { workflowParts: ['workflowsAll.skidata_arena_c003bd0', this.persistedEnabled ? 'workflowsAll.enabled_55b28e8' : 'workflowsAll.disabled_57e2379', 'workflowsAll.for_this_event_f0bf53d'] },
          color: 'primary',
        }
        this.confirmationOpen = false
        this.requestedEnabled = null
      } catch (error) {
        const graphQLError = error && error.graphQLErrors && error.graphQLErrors[0]
        this.snackbar = {
          open: true,
          messageProp:
            (graphQLError && graphQLError.message) ||
            (error && error.message) ||
            'workflowsAll.could_not_update_the_skidata_arena_setting_24745e1',
          color: 'error',
        }
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped>
.skidata-event-toggle {
  margin-top: 2rem;
  padding: 1.5rem;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
}

.skidata-event-toggle__row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1.5rem;
}

.skidata-event-toggle__title {
  margin: 0;
  color: #ff4c00;
  font-family: Raleway;
  font-size: 1.25rem;
  font-weight: 500;
}

.skidata-event-toggle__description,
.skidata-event-toggle__admin-note {
  margin: 0.5rem 0 0;
  color: #666666;
  font-family: 'Source Sans Pro';
  font-size: 1rem;
  line-height: 1.5;
}

.skidata-event-toggle__admin-note {
  color: #979797;
  font-size: 0.875rem;
}

.skidata-event-toggle__switch {
  flex: 0 0 auto;
  margin: 0;
  padding: 0;
}

.skidata-event-toggle__dialog-title {
  font-family: Raleway;
  font-weight: 600;
}

@media (max-width: 600px) {
  .skidata-event-toggle__row {
    gap: 1rem;
  }
}
</style>
